import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import {
  Bell, Search, Filter, CheckCheck, Trash2, Settings, RefreshCw, EyeOff, Eye, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import HorizontalScrollContainer from "../components/HorizontalScrollContainer";
import { CATEGORIES, CATEGORY_META, getCategoryMeta } from "../lib/notificationCategories";
import { useAuth } from "../contexts/AuthContext";
import StickyPageHeader from "../components/StickyPageHeader";

export default function Notifications() {
  const { user } = useAuth();
  const isManagerOrAdmin = user?.role === "admin" || user?.role === "manager";

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, by_category: {} });
  const [loading, setLoading] = useState(false);

  // Filtres
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [includeMuted, setIncludeMuted] = useState(false);

  // Sélection multi
  const [selected, setSelected] = useState(new Set());

  // Préférences
  const [prefs, setPrefs] = useState({ muted_categories: [], muted_toast_categories: [], limit_to_my_brands: false });
  const [prefsOpen, setPrefsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (q) params.q = q;
      if (category) params.category = category;
      if (status && status !== "all") params.status = status;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (includeMuted) params.include_muted = true;
      const [list, st, pf] = await Promise.all([
        api.get("/notifications", { params }),
        api.get("/notifications/stats"),
        api.get("/notifications/preferences"),
      ]);
      setItems(list.data || []);
      setStats(st.data || { total: 0, unread: 0, by_category: {} });
      setPrefs(pf.data || { muted_categories: [], muted_toast_categories: [], limit_to_my_brands: false });
      setSelected(new Set());
    } catch (e) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, category, status, fromDate, toDate, includeMuted]);

  const toggleSelected = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const allVisibleSelected = items.length > 0 && items.every((n) => selected.has(n.id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) setSelected(new Set());
    else setSelected(new Set(items.map((n) => n.id)));
  };

  const markSelectedRead = async () => {
    if (selected.size === 0) return;
    try {
      await api.put("/notifications/mark-read-bulk", { ids: Array.from(selected) });
      toast.success(`${selected.size} notification(s) marquée(s) comme lue(s)`);
      load();
      try { window.dispatchEvent(new CustomEvent("plania:notifications-changed")); } catch { /* noop */ }
    } catch { toast.error("Erreur"); }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Supprimer définitivement ${selected.size} notification(s) ?`)) return;
    try {
      await api.delete("/notifications/bulk", { data: { ids: Array.from(selected) } });
      toast.success(`${selected.size} supprimée(s)`);
      load();
      try { window.dispatchEvent(new CustomEvent("plania:notifications-changed")); } catch { /* noop */ }
    } catch { toast.error("Erreur"); }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Supprimer cette notification ?")) return;
    try {
      await api.delete(`/notifications/${id}`);
      setItems((prev) => prev.filter((n) => n.id !== id));
      toast.success("Supprimée");
      try { window.dispatchEvent(new CustomEvent("plania:notifications-changed")); } catch { /* noop */ }
    } catch { toast.error("Erreur"); }
  };

  const markOneRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      try { window.dispatchEvent(new CustomEvent("plania:notifications-changed")); } catch { /* noop */ }
    } catch { /* noop */ }
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/mark-all-read");
      toast.success("Toutes les notifications marquées lues");
      load();
      try { window.dispatchEvent(new CustomEvent("plania:notifications-changed")); } catch { /* noop */ }
    } catch { toast.error("Erreur"); }
  };

  const savePrefs = async (next) => {
    try {
      const res = await api.put("/notifications/preferences", next);
      setPrefs(res.data);
      toast.success("Préférences enregistrées");
      // Recharger pour appliquer le filtrage muted
      load();
    } catch { toast.error("Erreur"); }
  };

  const resetFilters = () => {
    setQ(""); setCategory(""); setStatus("all"); setFromDate(""); setToDate(""); setIncludeMuted(false);
  };

  return (
    <div className="space-y-6" data-testid="notifications-center">
      <StickyPageHeader testid="notifications-sticky-header">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-uppercase mb-2">Espace personnel</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900 flex items-center gap-3">
            <Bell size={28} strokeWidth={1.5} className="text-[#0055FF]" />
            Centre de notifications
          </h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            <span className="text-[#0055FF] font-semibold">{stats.total}</span> au total · <span className="text-red-600 font-semibold">{stats.unread}</span> non lue(s)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="notif-refresh-btn">
            <RefreshCw size={13} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
          <PreferencesDialog
            open={prefsOpen}
            onOpenChange={setPrefsOpen}
            prefs={prefs}
            onSave={savePrefs}
            isManagerOrAdmin={isManagerOrAdmin}
          />
        </div>
      </div>

      {/* Statistiques par catégorie */}
      <CategoryStats stats={stats} mutedCategories={prefs.muted_categories} onPickCategory={setCategory} active={category} />

      {/* Filtres */}
      <div className="bg-white border border-zinc-200 rounded-sm p-4 space-y-3" data-testid="notif-filters">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <Label className="label-uppercase mb-1 block">Recherche</Label>
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <Input
                className="pl-7"
                placeholder="Titre, message…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="notif-search"
              />
            </div>
          </div>
          <div>
            <Label className="label-uppercase mb-1 block">Catégorie</Label>
            <Select value={category || "__all"} onValueChange={(v) => setCategory(v === "__all" ? "" : v)}>
              <SelectTrigger data-testid="notif-category-filter"><SelectValue placeholder="Toutes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Toutes</SelectItem>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{CATEGORY_META[c].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-uppercase mb-1 block">Statut</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="notif-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="unread">Non lues</SelectItem>
                <SelectItem value="read">Lues</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-uppercase mb-1 block">Du</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="notif-from-date" />
            </div>
            <div>
              <Label className="label-uppercase mb-1 block">Au</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="notif-to-date" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
          <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
            <Checkbox
              checked={includeMuted}
              onCheckedChange={setIncludeMuted}
              data-testid="notif-include-muted"
            />
            Inclure les catégories désactivées dans mes préférences
          </label>
          <Button variant="ghost" size="sm" onClick={resetFilters} className="text-zinc-600" data-testid="notif-reset-filters">
            <Filter size={12} className="mr-1" /> Réinitialiser
          </Button>
        </div>
      </div>

      {/* Actions groupées */}
      <div className="flex items-center justify-between flex-wrap gap-2 bg-zinc-50 border border-zinc-200 rounded-sm px-4 py-2">
        <div className="flex items-center gap-3">
          <Checkbox
            checked={allVisibleSelected}
            onCheckedChange={toggleSelectAll}
            data-testid="notif-select-all"
          />
          <span className="text-xs font-mono text-zinc-600">
            {selected.size > 0 ? `${selected.size} sélectionnée(s)` : `${items.length} affichée(s)`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm" disabled={selected.size === 0}
            onClick={markSelectedRead}
            data-testid="notif-bulk-read"
          >
            <CheckCheck size={12} className="mr-1" /> Marquer comme lu
          </Button>
          <Button
            variant="outline" size="sm" disabled={selected.size === 0}
            onClick={deleteSelected}
            className="text-red-700 border-red-200 hover:bg-red-50"
            data-testid="notif-bulk-delete"
          >
            <Trash2 size={12} className="mr-1" /> Supprimer
          </Button>
          <Button
            variant="ghost" size="sm" onClick={markAllRead}
            disabled={stats.unread === 0}
            data-testid="notif-mark-all-read"
          >
            <CheckCheck size={12} className="mr-1" /> Tout marquer lu
          </Button>
        </div>
      </div>
      </StickyPageHeader>

      {/* Liste */}
      <HorizontalScrollContainer>
        <table className="w-full min-w-[900px] bg-white border border-zinc-200 rounded-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left label-uppercase p-3 w-10"></th>
              <th className="text-left label-uppercase p-3 w-32">Catégorie</th>
              <th className="text-left label-uppercase p-3">Titre / Message</th>
              <th className="text-left label-uppercase p-3 w-44">Date</th>
              <th className="text-left label-uppercase p-3 w-24">Statut</th>
              <th className="text-right label-uppercase p-3 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((n) => {
              const meta = getCategoryMeta(n.category);
              const isMuted = prefs.muted_categories.includes(n.category);
              return (
                <tr
                  key={n.id}
                  className={`border-b border-zinc-100 hover:bg-zinc-50 ${n.read ? "opacity-75" : ""}`}
                  data-testid={`notif-row-${n.id}`}
                >
                  <td className="p-3">
                    <Checkbox
                      checked={selected.has(n.id)}
                      onCheckedChange={() => toggleSelected(n.id)}
                      data-testid={`notif-select-${n.id}`}
                    />
                  </td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider ${meta.bg} ${meta.border} ${meta.text}`}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
                      {meta.label}
                    </span>
                    {isMuted && (
                      <span className="ml-1 inline-flex items-center" title="Catégorie désactivée">
                        <EyeOff size={11} className="text-zinc-400" />
                      </span>
                    )}
                  </td>
                  <td className="p-3 max-w-2xl">
                    <p className={`text-sm ${n.read ? "text-zinc-600" : "font-medium text-zinc-900"}`}>{n.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{n.message}</p>
                  </td>
                  <td className="p-3 text-xs font-mono text-zinc-500">
                    {new Date(n.created_at).toLocaleString("fr-FR", {
                      day: "2-digit", month: "2-digit", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </td>
                  <td className="p-3">
                    {n.read ? (
                      <span className="text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider bg-zinc-50 border-zinc-200 text-zinc-600">Lue</span>
                    ) : (
                      <span className="text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider bg-sky-50 border-sky-200 text-sky-700">Non lue</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {!n.read && (
                        <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => markOneRead(n.id)} data-testid={`notif-mark-read-${n.id}`}>
                          <Eye size={11} />
                        </Button>
                      )}
                      <Button
                        variant="outline" size="sm" className="h-7 px-2 text-red-700 border-red-200 hover:bg-red-50"
                        onClick={() => deleteOne(n.id)}
                        data-testid={`notif-delete-${n.id}`}
                      >
                        <Trash2 size={11} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center p-8 text-sm text-zinc-500 font-mono">
                  {loading ? "Chargement…" : "Aucune notification ne correspond à ces filtres"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </HorizontalScrollContainer>
    </div>
  );
}

/* ---------------- Stats cards par catégorie ---------------- */

function CategoryStats({ stats, mutedCategories, onPickCategory, active }) {
  const cats = useMemo(() => CATEGORIES.filter((c) => (stats.by_category?.[c]?.total || 0) > 0), [stats]);
  if (cats.length === 0) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="notif-category-stats">
      {cats.map((c) => {
        const meta = CATEGORY_META[c];
        const counts = stats.by_category[c] || { total: 0, unread: 0 };
        const isMuted = mutedCategories.includes(c);
        const isActive = active === c;
        return (
          <button
            key={c}
            onClick={() => onPickCategory(isActive ? "" : c)}
            className={`text-left border rounded-sm p-2.5 transition-colors ${
              isActive ? `${meta.bg} ${meta.border} ring-2 ring-offset-1 ring-zinc-900/10`
                       : `bg-white border-zinc-200 hover:${meta.bg}`
            } ${isMuted ? "opacity-60" : ""}`}
            data-testid={`notif-cat-stat-${c}`}
          >
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: meta.color }} />
              <span className="text-[10px] uppercase font-semibold tracking-wider text-zinc-700 truncate">{meta.label}</span>
              {isMuted && <EyeOff size={10} className="text-zinc-400 ml-auto" />}
            </div>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="text-lg font-bold text-zinc-900 font-mono">{counts.total}</span>
              {counts.unread > 0 && (
                <span className="text-[10px] font-mono text-red-600">+{counts.unread} non lue(s)</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- Preferences Dialog ---------------- */

function PreferencesDialog({ open, onOpenChange, prefs, onSave, isManagerOrAdmin }) {
  const [local, setLocal] = useState(prefs);
  useEffect(() => { setLocal(prefs); }, [prefs, open]);

  const toggle = (key, cat) => {
    const arr = new Set(local[key] || []);
    if (arr.has(cat)) arr.delete(cat); else arr.add(cat);
    setLocal({ ...local, [key]: Array.from(arr) });
  };

  const save = () => { onSave(local); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="notif-prefs-btn">
          <Settings size={13} className="mr-1" /> Préférences
        </Button>
      </DialogTrigger>
      <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-2xl" data-testid="notif-prefs-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
          <DialogTitle>Préférences de notifications</DialogTitle>
          <DialogDescription className="sr-only">
            Personnalisez la visibilité des catégories, les toasts en temps réel et la portée géographique.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto px-6 py-4 flex-1 space-y-5">
          <div className="bg-sky-50 border border-sky-200 rounded-sm p-3 text-xs font-mono text-sky-900">
            <AlertTriangle size={12} className="inline mr-1 mb-0.5" />
            Vos choix masquent les notifications dans l'affichage mais ne suppriment pas leur création.
            Désactivez la coche « Inclure les catégories désactivées » pour cacher complètement une catégorie du centre.
          </div>

          <div>
            <h3 className="text-sm font-semibold text-zinc-900 mb-1">Visibilité (centre + cloche)</h3>
            <p className="text-xs text-zinc-500 font-mono mb-3">Décochez pour masquer une catégorie de l'affichage par défaut.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CATEGORIES.map((c) => {
                const meta = CATEGORY_META[c];
                const muted = (local.muted_categories || []).includes(c);
                return (
                  <label
                    key={c}
                    className={`flex items-center justify-between gap-2 border rounded-sm px-3 py-2 cursor-pointer ${muted ? "bg-zinc-50 border-zinc-200" : `${meta.bg} ${meta.border}`}`}
                    data-testid={`prefs-visibility-${c}`}
                  >
                    <span className="inline-flex items-center gap-2 text-sm text-zinc-800">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta.color }} />
                      {meta.label}
                    </span>
                    <Switch
                      checked={!muted}
                      onCheckedChange={() => toggle("muted_categories", c)}
                      data-testid={`prefs-visibility-switch-${c}`}
                    />
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-zinc-900 mb-1">Toasts temps réel</h3>
            <p className="text-xs text-zinc-500 font-mono mb-3">Désactivez le pop-up live pour certaines catégories (la notification reste visible dans le centre).</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CATEGORIES.map((c) => {
                const meta = CATEGORY_META[c];
                const muted = (local.muted_toast_categories || []).includes(c);
                return (
                  <label
                    key={c}
                    className="flex items-center justify-between gap-2 border border-zinc-200 rounded-sm px-3 py-2 cursor-pointer hover:bg-zinc-50"
                    data-testid={`prefs-toast-${c}`}
                  >
                    <span className="inline-flex items-center gap-2 text-sm text-zinc-800">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta.color }} />
                      {meta.label}
                    </span>
                    <Switch
                      checked={!muted}
                      onCheckedChange={() => toggle("muted_toast_categories", c)}
                      data-testid={`prefs-toast-switch-${c}`}
                    />
                  </label>
                );
              })}
            </div>
          </div>

          {isManagerOrAdmin && (
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 mb-1">Portée géographique</h3>
              <label className="flex items-center justify-between gap-2 border border-zinc-200 rounded-sm px-3 py-2 cursor-pointer hover:bg-zinc-50">
                <span className="text-sm text-zinc-800">
                  Limiter aux notifications de mes départements
                  <span className="block text-[11px] text-zinc-500 font-mono mt-0.5">Masque les notifs liées à des départements auxquels vous n'êtes pas rattaché.</span>
                </span>
                <Switch
                  checked={!!local.limit_to_my_brands}
                  onCheckedChange={(v) => setLocal({ ...local, limit_to_my_brands: v })}
                  data-testid="prefs-limit-to-brands"
                />
              </label>
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="prefs-save-btn">
            Enregistrer mes préférences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
